import { createTheme } from '@mui/material';

const BORDER_RADIUS = 6;
const SPACING = 8;

enum FontWeight {
  Thin = 300,
  Default = 500,
  SemiBold = 600,
  Bold = 800,
  Black = 900,
}

export enum Color {
  White = '#FFFFFF',
  Black = '#000000',
  Transparent = 'rgba(255, 255, 255, 0.06)',

  // Primaries
  JungleGreen = '#0E2B2E',
  JungleGreenLight = 'rgba(14, 43, 46, 0.85)',
  ElectricGreen = '#50FB60',
  SeaGreen = '#285F54',
  SeaGreenLight = 'rgba(40, 95, 84, 0.25)',
  SeaGreenLighter = 'rgba(40, 95, 84, 0.1)',
  SeaGreenLightest = 'rgba(40, 95, 84, 0.05)',

  // Alerts
  SuccessBackground = '#F4F7F7',
  SuccessBackgroundLight = 'rgba(92, 159, 71, 0.05)',
  ErrorBackground = '#FCE7E8',
  ErrorBackgroundLight = 'rgba(228, 17, 28, 0.1)',

  // Backdrop
  BackdropBackground = 'rgba(0,0,0,0.6)',

  // Gray
  Gray0 = '#f9f9f9',
  Gray1 = '#ededee',
  Gray2 = '#e0e1e1',
  Gray3 = '#d2d3d3',
  Gray4 = '#c2c4c4',
  Gray5 = '#b1b3b4',
  Gray6 = '#9da0a1',
  Gray7 = '#868a8a',
  Gray8 = '#686d6e',
  Gray9 = '#3a4041',

  // Error
  Error1 = '#E4111C',
  Error2 = '#FBD0D2',
  Error4 = '#F47177',
  Error5 = '#F1414A',
  Error6 = '#B40D16',
  Error7 = '#850A10',
  Error8 = '#55060A',
  Error9 = '#260305',

  // Success
  Success0 = '#5C9F47',
  Success7 = '#04AF14',
  Success8 = '#037D0E',

  // Warning
  Warn0 = '#FFF7E6',
  Warn1 = '#FFDA8F',
  Warn2 = '#FFC652',
  Warn3 = '#FFBC33',
  Warn4 = '#FFB31A',
  Warn5 = '#F6A500',
  Warn6 = '#CC8900',
}

export const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 2000,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          // Custom scrollbar styles
          '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
            width: 6,
            height: 6,
            backgroundColor: Color.Gray0,
          },
          '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
            borderRadius: 8,
            minHeight: 6,
            border: `3px solid ${Color.SeaGreen}`,
          },
          '&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus': {
            backgroundColor: Color.Gray0,
          },
          '&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active': {
            backgroundColor: Color.Gray0,
          },
          '&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover': {
            backgroundColor: Color.Gray0,
          },
          '&::-webkit-scrollbar-corner, & *::-webkit-scrollbar-corner': {
            backgroundColor: Color.Gray0,
            borderRadius: 8,
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: 'contained',
        sx: {
          textTransform: 'none',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiSwitch: {
      defaultProps: {
        size: 'small',
        sx: { marginRight: 1 },
      },
    },
    MuiTypography: {
      defaultProps: {
        fontWeight: FontWeight.Default,
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small',
      },
    },
  },
  spacing: SPACING,
  palette: {
    mode: 'light',
    text: {
      primary: Color.Gray9,
      secondary: Color.Gray6,
    },
    primary: {
      main: Color.JungleGreen,
      contrastText: Color.White,
    },
    secondary: {
      main: Color.ElectricGreen,
      contrastText: Color.SeaGreen,
    },
    success: {
      main: Color.Success0,
      contrastText: Color.White,
    },
    error: {
      main: Color.Error1,
      contrastText: Color.White,
    },
    warning: {
      main: Color.Warn4,
      contrastText: Color.White,
    },
    info: {
      main: Color.White,
    },
    common: {
      white: Color.White,
      black: Color.Black,
    },
  },
  shape: {
    borderRadius: BORDER_RADIUS,
  },
  typography: {
    fontWeightRegular: FontWeight.Default,
    fontWeightMedium: FontWeight.SemiBold,
    fontWeightBold: FontWeight.Bold,
  },
});
